import React, { FC, useCallback, useEffect, useState } from "react"
import { KSpan, KTitleSpan, KInput, KDropdown, KButton, KTooltip, KSelectRange } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from "chart.js"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { Bar } from "react-chartjs-2"
import { toast } from "react-toastify"
import { handleAuthorizedRequest, users } from "../../../../../apis"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../../../constants"
import { mapToCamelCase, addCommasToNumber, formatNumber, dateToYYYYMM } from "../../../../../utility"
import {
  AdvanceDealRow,
  AdvanceDealTrend,
  AdvancePortfolioKpi,
  AdvancePortfolioTable,
  DistributionPortfolioTable,
  AdvancePortfolioSummaryKpi,
  DistributionDealRow,
  DistributionSummaryMetrics
} from "../../../../../types"
import Loading from "../../../../ui/Loading"
import Pagination from "../../../../ui/table/Pagination"
import SearchUserComponent from "../../../../ui/components/SearchUserComponent"
import { DateRangeType } from "kahuna-base-react-components/dist/components/KSelectRange/KSelectRange"
import AdvanceDealDetailPage from "./AdvanceDealDetailPage"

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const GREEN = "#22c55e"
const RED = "#ef4444"
const TEXT_MUTED = "#6b7280"
const TEXT_PRIMARY = "#111"
const CHART_FONT = "Inter var, Inter, system-ui, -apple-system, sans-serif"

const RoyaltyDateOptionList: KSelectOption[] = [
  { label: "Activity Period", value: 1, value2: "activity_period" },
  { label: "Reporting Period", value: 2, value2: "reporting_period" }
]

type AdminPortfolioOverviewProps = {
  setOpenSidebar: (openSidebar: boolean) => void
  openSidebar: boolean
}

// ---- formatting -------------------------------------------------------------

const usd0 = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `$${addCommasToNumber(Math.round(value))}`

const compactUsd = (value: number) => {
  if (Math.abs(value) >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`
  if (Math.abs(value) >= 1_000) return `$${Math.round(value / 1_000)}K`
  return `$${Math.round(value)}`
}

const pct = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${parseFloat(value.toFixed(digits))}%`

const multipleX = (value: number | null | undefined) =>
  value === null || value === undefined || isNaN(value) ? "N/A" : `${value.toFixed(2)}x`

const xirrPct = (fraction: number | null | undefined) =>
  fraction === null || fraction === undefined || isNaN(fraction) ? "N/A" : `${(fraction * 100).toFixed(0)}%`

const monthYear = (key: string | null | undefined) => {
  if (!key) return "N/A"
  // backend deal_date / contract_date are "YYYYMM"
  const year = key.slice(0, 4)
  const month = parseInt(key.slice(4, 6), 10)
  if (!year || isNaN(month)) return "N/A"
  const d = new Date(parseInt(year, 10), month - 1, 1)
  return d.toLocaleDateString("en-US", { month: "short", year: "numeric" })
}

const toLocaleDate = (date: Date) => {
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" })
}

// ---- small components -------------------------------------------------------

const KpiCard: FC<{ label: string; value: string; subtext?: string; subtextColor?: string }> = ({
  label,
  value,
  subtext,
  subtextColor = TEXT_MUTED
}) => (
  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-1.5">
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    <KTitleSpan text={value} fontSize={24} fontWeight={700} color={TEXT_PRIMARY} />
    {subtext ? <KSpan text={subtext} fontSize={12} color={subtextColor} /> : null}
  </div>
)

const BlurredKpiCard: FC<{
  label: string
  isLoading: boolean
  onClick: () => void
}> = ({ label, isLoading, onClick }) => (
  <div
    className={`bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col gap-1.5 ${
      isLoading ? "" : "cursor-pointer hover:border-black transition-colors"
    }`}
    onClick={isLoading ? undefined : onClick}
  >
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    {isLoading ? (
      <div className="flex items-center gap-2 h-[33px]">
        <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" />
        <KSpan text="Calculating..." fontSize={12} color={TEXT_MUTED} />
      </div>
    ) : (
      <div className="relative">
        <KTitleSpan text="---.--" fontSize={24} fontWeight={700} color={TEXT_PRIMARY} />
        <div className="absolute inset-0 backdrop-blur-sm bg-white/50 flex items-center rounded">
          <KSpan text="Click to calculate" fontSize={12} color={TEXT_MUTED} fontWeight={500} />
        </div>
      </div>
    )}
  </div>
)

const SummaryCard: FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex-1 p-3.5 border border-gray-100 rounded-xl bg-gray-50 text-center">
    <KSpan text={label} fontSize={11} fontWeight={600} color={TEXT_MUTED} letterSpacing="0.05em" />
    <div className="mt-1">
      <KTitleSpan text={value} fontSize={20} fontWeight={700} color={TEXT_PRIMARY} />
    </div>
  </div>
)

const trendGlyph = (trend: AdvanceDealTrend) => {
  if (trend === "up") return { char: "↗", color: GREEN }
  if (trend === "down") return { char: "↘", color: RED }
  return { char: "→", color: TEXT_MUTED }
}

const TrendCell: FC<{ trend: AdvanceDealTrend }> = ({ trend }) => {
  const g = trendGlyph(trend)
  return <span style={{ color: g.color, fontSize: 16, fontWeight: 700 }}>{g.char}</span>
}

const STATUS_STYLE: { [key: string]: { label: string; bg: string; color: string } } = {
  in_progress_on_track: { label: "In Progress - On Track", bg: "#dcfce7", color: "#15803d" },
  in_progress_watchlist: { label: "In Progress - Watchlist", bg: "#fef3c7", color: "#b45309" },
  in_progress_delayed: { label: "In Progress - Delayed", bg: "#ffedd5", color: "#c2410c" },
  in_progress_impaired: { label: "In Progress - Impaired", bg: "#fee2e2", color: "#b91c1c" },
  in_progress_npl: { label: "In Progress - NPL", bg: "#ff4242", color: "#ededed" },
  recouped_on_track: { label: "Recouped - On Track", bg: "#dcfce7", color: "#15803d" },
  recouped_watchlist: { label: "Recouped - Watchlist", bg: "#fef3c7", color: "#b45309" },
  recouped_delayed: { label: "Recouped - Delayed", bg: "#ffedd5", color: "#c2410c" },
  recouped_impaired: { label: "Recouped - Impaired", bg: "#fee2e2", color: "#b91c1c" },
  recouped_npl: { label: "Recouped - NPL", bg: "#ff4242", color: "#ededed" }
}

const StatusBadge: FC<{ status: string }> = ({ status }) => {
  const s = STATUS_STYLE[status] || { label: status, bg: "#f1f5f9", color: TEXT_MUTED }
  return (
    <span
      style={{
        backgroundColor: s.bg,
        color: s.color,
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap"
      }}
    >
      {s.label}
    </span>
  )
}

const ArtistCell: FC<{ name: string; image: string | null; onClick?: () => void }> = ({ name, image, onClick }) => (
  <div
    style={{ display: "flex", alignItems: "center", gap: 10, cursor: onClick ? "pointer" : "default" }}
    onClick={onClick}
  >
    <div
      style={{
        width: 28,
        height: 28,
        borderRadius: "50%",
        background: image ? `center / cover no-repeat url(${image})` : "#e2e8f0",
        flexShrink: 0
      }}
    />
    <KSpan text={name} fontSize={13} fontWeight={500} color={TEXT_PRIMARY} ellipsis />
  </div>
)

const PAGE_SIZE = 10

const ExcludeToggleCell: FC<{
  userId: number
  excluded: boolean
  isAdvanceDeal: boolean
  onToggle: (userId: number, excluded: boolean, isAdvanceDeal: boolean) => void
}> = ({ userId, excluded, isAdvanceDeal, onToggle }) => (
  <input
    type="checkbox"
    checked={excluded}
    title="Exclude from portfolio"
    onChange={() => onToggle(userId, excluded, isAdvanceDeal)}
    style={{ accentColor: "#111", cursor: "pointer" }}
  />
)

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  fontSize: 11,
  fontWeight: 600,
  color: TEXT_MUTED,
  letterSpacing: "0.03em",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap"
}

const tdStyle: React.CSSProperties = {
  padding: "12px",
  fontSize: 13,
  fontFamily: "Inter",
  color: TEXT_PRIMARY,
  borderBottom: "1px solid #f1f5f9",
  whiteSpace: "nowrap"
}

// ---- charts -----------------------------------------------------------------

const RecoupTimeChart: FC<{ portfolio: AdvancePortfolioKpi }> = ({ portfolio }) => {
  const buckets = portfolio.recoupTimeChart.buckets
  const data = {
    labels: buckets.map((b) => b.bucket),
    datasets: [
      {
        data: buckets.map((b) => b.companyBalance),
        backgroundColor: TEXT_PRIMARY,
        borderRadius: 6,
        maxBarThickness: 56
      }
    ]
  }
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    // Reserve space at the top so the "X deals" datalabel above the tallest bar
    // isn't clipped when that bar reaches the top of the y-axis.
    layout: { padding: { top: 24 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => compactUsd(ctx.parsed.y),
          afterLabel: (ctx: any) => `${buckets[ctx.dataIndex].dealCount} deals`
        }
      },
      datalabels: {
        anchor: "end",
        align: "end",
        offset: 2,
        color: TEXT_PRIMARY,
        font: { size: 11, weight: 600, family: CHART_FONT },
        formatter: (_v: number, ctx: any) => `${buckets[ctx.dataIndex].dealCount} deals`
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { family: CHART_FONT, size: 11 } } },
      y: {
        beginAtZero: true,
        grace: "8%",
        ticks: { callback: (v: any) => compactUsd(Number(v)), font: { family: CHART_FONT, size: 11 } },
        grid: { color: "#f1f5f9" },
        title: { display: true, text: "Company Balance ($K)", font: { family: CHART_FONT, size: 11 } }
      }
    }
  }
  return <Bar data={data} options={options} plugins={[ChartDataLabels as any]} />
}

const MonthlyChart: FC<{ portfolio: AdvancePortfolioKpi }> = ({ portfolio }) => {
  const months = portfolio.monthlyChart.months
  const labels = months.map((m) => monthYear(m.month).split(" ")[0])
  const data = {
    labels,
    datasets: [
      {
        label: "Deployed",
        data: months.map((m) => m.deployed),
        backgroundColor: TEXT_PRIMARY,
        borderRadius: 4,
        maxBarThickness: 22
      },
      {
        label: "Recouped",
        data: months.map((m) => m.recouped),
        backgroundColor: GREEN,
        borderRadius: 4,
        maxBarThickness: 22
      }
    ]
  }
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top", align: "end", labels: { boxWidth: 10, font: { family: CHART_FONT, size: 11 } } },
      tooltip: { callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${compactUsd(ctx.parsed.y)}` } },
      datalabels: { display: false }
    },
    scales: {
      x: { grid: { display: false }, ticks: { font: { family: CHART_FONT, size: 11 } } },
      y: {
        beginAtZero: true,
        ticks: { callback: (v: any) => compactUsd(Number(v)), font: { family: CHART_FONT, size: 11 } },
        grid: { color: "#f1f5f9" }
      }
    }
  }
  return <Bar data={data} options={options} />
}

const DealStatusOptions: KSelectOption[] = [
  { label: "In Progress - On Track", value: 1, value2: "in_progress_on_track" },
  { label: "In Progress - Watchlist", value: 2, value2: "in_progress_watchlist" },
  { label: "In Progress - Delayed", value: 3, value2: "in_progress_delayed" },
  { label: "In Progress - Impaired", value: 4, value2: "in_progress_impaired" },
  { label: "In Progress - NPL", value: 5, value2: "in_progress_npl" },
  { label: "Recouped - On Track", value: 6, value2: "recouped_on_track" },
  { label: "Recouped - Watchlist", value: 7, value2: "recouped_watchlist" },
  { label: "Recouped - Delayed", value: 8, value2: "recouped_delayed" },
  { label: "Recouped - Impaired", value: 9, value2: "recouped_impaired" },
  { label: "Recouped - NPL", value: 10, value2: "recouped_npl" }
]

const RecoupAgeOptions: KSelectOption[] = [
  { label: "<6 mo", value: 1, value2: "-999999,6" },
  { label: "6-12 mo", value: 2, value2: "6,12" },
  { label: "12–18 mo", value: 3, value2: "12,18" },
  { label: "18–24 mo", value: 4, value2: "18,24" },
  { label: "24+ mo", value: 5, value2: "24,999999" }
]

const ExposureOptions: KSelectOption[] = [
  { label: "High (> $100K)", value: 1, value2: "100000,99999999" },
  { label: "Medium ($25K–$100K)", value: 2, value2: "25000,100000" },
  { label: "Low (< $25K)", value: 3, value2: "0,25000" }
]

const ContractExpiryOptions: KSelectOption[] = [
  { label: "Expiring within 30 Days", value: 1, value2: "30" },
  { label: "Expiring within 60 Days", value: 2, value2: "60" },
  { label: "Expiring within 90 Days", value: 3, value2: "90" },
  { label: "Expiring within 6 Months", value: 4, value2: "180" },
  { label: "Expiring within 12 Months", value: 5, value2: "365" }
]

const AccountTypeOptions: KSelectOption[] = [
  { label: "Advance", value: 1, value2: "advance" },
  { label: "Distribution", value: 2, value2: "distribution" }
]

const DistributionFeeOptions: KSelectOption[] = [
  { label: "< 10%", value: 1, value2: "0,10" },
  { label: "10–15%", value: 2, value2: "10,15" },
  { label: "15-20%", value: 3, value2: "15,20" },
  { label: "20-25%", value: 4, value2: "20,25" },
  { label: "25-30%", value: 5, value2: "25,30" },
  { label: "30-35%", value: 6, value2: "30,35" },
  { label: "> 35%", value: 7, value2: "35,100" }
]

const FilterDropdown: FC<{
  label: string
  selected: KSelectOption | KSelectOption[] | undefined
  options: KSelectOption[]
  isMulti: boolean
  width: number
  menuWidth: number
  onSelect: (option: KSelectOption | KSelectOption[] | undefined) => void
}> = ({ label, selected, options, isMulti, onSelect, width, menuWidth }) => (
  <div className="flex gap-2">
    <div className="mt-1">
      <KSpan text={`${label}:`} fontSize={12} fontWeight={500} color={TEXT_MUTED} />
    </div>
    <KDropdown
      height={isMulti ? undefined : 36}
      width={width}
      menuWidth={menuWidth}
      closeMenuOnSelect={isMulti ? false : true}
      isMulti={isMulti}
      selected={selected}
      isClearable
      options={options}
      onSelect={(s) => onSelect(s as KSelectOption)}
    />
  </div>
)

const monthsText = (value: number | null | undefined) =>
  value === null || value === undefined ? "N/A" : `${Math.round(value)} mo`

// Company Recoup cell — estimated total months on top, realized below. A
// recouped deal shows its closing duration on the bottom row.
const CompanyRecoupCell: FC<{ row: AdvanceDealRow }> = ({ row }) => {
  if (row.recouped) {
    return (
      <>
        <KSpan text="Recouped" fontSize={13} fontWeight={500} color={GREEN} />
        <div>
          <KSpan text={`closed ${row.companyRecoupLag} mo`} fontSize={11} color={TEXT_MUTED} />
        </div>
      </>
    )
  }
  return (
    <>
      <KSpan text={`${row.companyRecoupLag} mo`} fontSize={13} />
    </>
  )
}

// Recoup Timing Delta = target − total recoup months (realized + estimation).
// Negative => recoup running past the deal target (shown red). For deals not
// yet recouped, the months still remaining are shown in parentheses.
const RecoupTimingDeltaCell: FC<{ row: AdvanceDealRow }> = ({ row }) => {
  const delta = row.recoupTimingDelta
  if (delta === null || delta === undefined) return <span style={{ color: TEXT_MUTED }}>N/A</span>
  const rounded = Math.round(delta)

  return (
    <>
      <KSpan text={`${rounded > 0 ? "+" : ""}${rounded} mo`} fontSize={13} color={rounded < 0 ? RED : GREEN} />
    </>
  )
}

const AdvanceDealsTable: FC<{
  rows: AdvanceDealRow[]
  count: number
  page: number
  statusFilter: KSelectOption[]
  royaltyDateFilter: KSelectOption | undefined
  ageFilter: KSelectOption | undefined
  exposureFilter: KSelectOption | undefined
  contractExpiryFilter: KSelectOption | undefined
  contractEndSort: "asc" | "desc" | undefined
  loading: boolean
  selectedUser: KSelectOption | undefined
  setSelectedUser: (selectedUser: KSelectOption | undefined) => void
  onRoyaltyDateChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onApplyFilter: () => void
  onStatusChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onAgeChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onExposureChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onContractExpiryChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onToggleContractEndSort: () => void
  onPageChange: (page: number) => void
  onToggleExclude: (userId: number, excluded: boolean, isAdvanceDeal: boolean) => void
  onSelectDeal: (selectedDeal: AdvanceDealRow) => void
}> = ({
  rows,
  count,
  page,
  royaltyDateFilter,
  statusFilter,
  ageFilter,
  selectedUser,
  exposureFilter,
  contractExpiryFilter,
  contractEndSort,
  loading,
  onStatusChange,
  onRoyaltyDateChange,
  onAgeChange,
  onApplyFilter,
  setSelectedUser,
  onExposureChange,
  onContractExpiryChange,
  onToggleContractEndSort,
  onPageChange,
  onToggleExclude,
  onSelectDeal
}) => {
  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE))
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <KTitleSpan text="Advance Deals" fontSize={16} fontWeight={700} />
        </div>
        <div className="flex gap-4">
          <FilterDropdown
            label="Period"
            width={160}
            menuWidth={180}
            isMulti={false}
            selected={royaltyDateFilter}
            options={RoyaltyDateOptionList}
            onSelect={onRoyaltyDateChange}
          />
          <FilterDropdown
            label="Status"
            width={260}
            menuWidth={240}
            isMulti={true}
            selected={statusFilter}
            options={DealStatusOptions}
            onSelect={onStatusChange}
          />
          <FilterDropdown
            isMulti={false}
            width={120}
            menuWidth={140}
            label="Recoup Age"
            selected={ageFilter}
            options={RecoupAgeOptions}
            onSelect={onAgeChange}
          />
          <FilterDropdown
            label="Exposure"
            width={200}
            menuWidth={240}
            isMulti={false}
            selected={exposureFilter}
            options={ExposureOptions}
            onSelect={onExposureChange}
          />
          <FilterDropdown
            label="Expiring"
            width={200}
            menuWidth={220}
            isMulti={false}
            selected={contractExpiryFilter}
            options={ContractExpiryOptions}
            onSelect={onContractExpiryChange}
          />
          <SearchUserComponent className="" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
          <KButton
            text="Apply Filters"
            background="black"
            textColor="white"
            onClick={() => onApplyFilter()}
            width="120px"
            height="36px"
          />
        </div>
      </div>
      <div style={{ overflowX: "auto", opacity: loading ? 0.5 : 1, transition: "opacity 0.15s" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={thStyle}>Artist</th>
              <th style={thStyle}>Deal Date</th>
              <th
                style={{ ...thStyle, cursor: "pointer", userSelect: "none" }}
                title="Sort by contract end date"
                onClick={onToggleContractEndSort}
              >
                {`Contract End ${contractEndSort === "asc" ? "▲" : contractEndSort === "desc" ? "▼" : ""}`}
              </th>
              <th style={thStyle}>Advance</th>
              <th style={thStyle}>Outstanding</th>
              <th style={thStyle}>Company Rec. %</th>
              <th style={thStyle}>Company Rec.</th>
              <th style={thStyle}>Target</th>
              <th style={thStyle}>Rec. Delta</th>
              <th style={thStyle}>Artist Rec.</th>
              <th style={thStyle}>12M IRR</th>
              <th style={thStyle}>24M IRR</th>
              <th style={thStyle}>36M IRR</th>
              <th style={thStyle}>XIRR</th>
              <th style={thStyle}>Trend</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Exclude</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.userId} style={{ opacity: row.excluded ? 0.45 : 1 }}>
                <td style={{ ...tdStyle, minWidth: 180 }}>
                  <ArtistCell name={row.artistName} image={row.artistImage} onClick={() => onSelectDeal(row)} />
                </td>
                <td style={tdStyle}>{monthYear(row.dealDate)}</td>
                <td style={tdStyle}>{monthYear(row.contractEnd)}</td>
                <td style={tdStyle}>{usd0(row.advance)}</td>
                <td style={tdStyle}>{usd0(row.outstanding)}</td>
                <td style={{ ...tdStyle, minWidth: 130 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ flex: 1, height: 6, background: "#f1f5f9", borderRadius: 999, minWidth: 60 }}>
                      <div
                        style={{
                          width: `${Math.min(100, row.companyRecoupPct)}%`,
                          height: "100%",
                          background: row.recouped ? GREEN : TEXT_MUTED,
                          borderRadius: 999
                        }}
                      />
                    </div>
                    <span style={{ fontSize: 12, color: TEXT_MUTED }}>{Math.round(row.companyRecoupPct)}%</span>
                  </div>
                </td>
                <td style={tdStyle}>
                  <CompanyRecoupCell row={row} />
                </td>
                <td style={tdStyle}>{monthsText(row.targetRecoupMonths)}</td>
                <td style={tdStyle}>
                  <RecoupTimingDeltaCell row={row} />
                </td>
                <td style={tdStyle}>{row.artistRecoupLag === null ? "N/A" : `${row.artistRecoupLag} mo`}</td>
                <td style={tdStyle}>{xirrPct(row.irr12M)}</td>
                <td style={tdStyle}>{xirrPct(row.irr24M)}</td>
                <td style={tdStyle}>{xirrPct(row.irr36M)}</td>
                <td style={tdStyle}>{xirrPct(row.xirr)}</td>
                <td style={tdStyle}>
                  <TrendCell trend={row.trend} />
                </td>
                <td style={tdStyle}>
                  <StatusBadge status={row.status} />
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <ExcludeToggleCell
                    userId={row.userId}
                    excluded={row.excluded}
                    isAdvanceDeal={true}
                    onToggle={onToggleExclude}
                  />
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td style={{ ...tdStyle, textAlign: "center", color: TEXT_MUTED }} colSpan={17}>
                  No advance deals
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination hidden={pageCount <= 1} currentPage={page} pageCount={pageCount} onClickPage={onPageChange} />
    </div>
  )
}

const renderPlatformStreamRow = (stream: number, platform: string) => {
  return (
    <div className="flex gap-1">
      <KSpan fontSize={13} text={`${platform}: `} />
      <KSpan fontSize={13} color={TEXT_PRIMARY} text={formatNumber(stream)} />
    </div>
  )
}

const renderPlatformRoyaltyRow = (royalty: number, platform: string) => {
  return (
    <div className="flex gap-1">
      <KSpan fontSize={13} text={`${platform}: `} />
      <KSpan
        fontSize={13}
        color={TEXT_PRIMARY}
        text={`$${royalty.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}
      />
    </div>
  )
}

const renderDistributionStreamBlock = (summaryMetric: DistributionSummaryMetrics) => {
  return (
    <td style={tdStyle}>
      <div className="flex flex-col gap-1">
        {renderPlatformStreamRow(summaryMetric.streamSpotify, "Spotify")}
        {renderPlatformStreamRow(summaryMetric.streamApple, "Apple")}
        {renderPlatformStreamRow(summaryMetric.streamYoutube, "Youtube")}
      </div>
    </td>
  )
}

const renderDistributionRoyaltyBlock = (summaryMetric: DistributionSummaryMetrics) => {
  return (
    <td style={tdStyle}>
      <div className="flex flex-col gap-1">
        {renderPlatformRoyaltyRow(summaryMetric.royaltySpotify, "Spotify")}
        {renderPlatformRoyaltyRow(summaryMetric.royaltyApple, "Apple")}
        {renderPlatformRoyaltyRow(summaryMetric.royaltyYoutube, "Youtube")}
      </div>
    </td>
  )
}

const renderDistributionRow = (
  row: DistributionDealRow,
  onToggleExclude: (userId: number, excluded: boolean, isAdvanceDeal: boolean) => void
) => {
  const kahunaLmRevenue = row.lm.totalAmount - row.lm.totalNet
  const kahunaLtmRevenue = row.ltm.totalAmount - row.ltm.totalNet
  const kahunaAllRevenue = row.all.totalAmount - row.all.totalNet

  return (
    <tr key={row.userId} style={{ opacity: row.excluded ? 0.45 : 1 }}>
      <td style={{ ...tdStyle, minWidth: 180 }}>
        <ArtistCell name={`${row.user?.firstName} ${row.user?.lastName || ""}`} image={row.user?.photo} />
      </td>
      <td style={tdStyle}>{monthYear(row.user?.dateJoined)}</td>
      <td style={tdStyle}>{row.userExpense?.distributionFee}</td>

      <td style={tdStyle}>{formatNumber(row.all.totalStream)}</td>
      <td style={tdStyle}>{`$${row.all.totalAmount.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${row.all.totalNet.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${kahunaAllRevenue.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      {renderDistributionStreamBlock(row.all)}
      {renderDistributionRoyaltyBlock(row.all)}

      <td style={tdStyle}>{formatNumber(row.ltm.totalStream)}</td>
      <td style={tdStyle}>{`$${row.ltm.totalAmount.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${row.ltm.totalNet.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${kahunaLtmRevenue.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      {renderDistributionStreamBlock(row.ltm)}
      {renderDistributionRoyaltyBlock(row.ltm)}

      <td style={tdStyle}>{formatNumber(row.lm.totalStream)}</td>
      <td style={tdStyle}>{`$${row.lm.totalAmount.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${row.lm.totalNet.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      <td style={tdStyle}>{`$${kahunaLmRevenue.toLocaleString("en-EN", { maximumFractionDigits: 2 })}`}</td>
      {renderDistributionStreamBlock(row.lm)}
      {renderDistributionRoyaltyBlock(row.lm)}

      <td style={{ ...tdStyle, textAlign: "center" }}>
        <ExcludeToggleCell
          userId={row.userId}
          excluded={row.excluded}
          isAdvanceDeal={false}
          onToggle={onToggleExclude}
        />
      </td>
    </tr>
  )
}

const DistributionDealsTable: FC<{
  rows: DistributionDealRow[]
  count: number
  page: number
  distroFee: KSelectOption | undefined
  accountType: KSelectOption | undefined
  loading: boolean
  selectedUser: KSelectOption | undefined
  dateValues: DateRangeType
  setSelectedUser: (selectedUser: KSelectOption | undefined) => void
  onAccountTypeChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  onApplyFilter: () => void
  onDistroFeeChange: (option: KSelectOption | KSelectOption[] | undefined) => void
  setDateValues: (dateValues: DateRangeType) => void
  onPageChange: (page: number) => void
  onToggleExclude: (userId: number, excluded: boolean, isAdvanceDeal: boolean) => void
}> = ({
  rows,
  count,
  page,
  selectedUser,
  distroFee,
  accountType,
  dateValues,
  loading,
  setDateValues,
  setSelectedUser,
  onAccountTypeChange,
  onApplyFilter,
  onDistroFeeChange,
  onPageChange,
  onToggleExclude
}) => {
  const pageCount = Math.max(1, Math.ceil(count / PAGE_SIZE))
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <KTitleSpan text="Distribution Deals" fontSize={16} fontWeight={700} />
        </div>
        <div className="flex gap-4">
          <div className="flex items-center">
            {
              //@ts-ignore
              dateValues?.[0] && <KSpan text={`${toLocaleDate(dateValues[0])} - ${toLocaleDate(dateValues[1])}`} />
            }
          </div>
          <KSelectRange
            key={`date_filter`}
            value={dateValues}
            onChange={(event) => setDateValues(event)}
            height="36px"
            width="auto"
            backgroundColor="#F7F7F7"
            hoverBackgroundColor="#F3F3F3"
            boxShadow="none"
            align={"center"}
          />
          <FilterDropdown
            label="Account Type"
            isMulti={false}
            width={120}
            menuWidth={160}
            selected={accountType}
            options={AccountTypeOptions}
            onSelect={onAccountTypeChange}
          />
          <FilterDropdown
            label="Distribution Fee"
            isMulti={false}
            width={120}
            menuWidth={160}
            selected={distroFee}
            options={DistributionFeeOptions}
            onSelect={onDistroFeeChange}
          />
          <SearchUserComponent className="" selectedUser={selectedUser} setSelectedUser={setSelectedUser} />
          <KButton
            text="Apply Filters"
            background="black"
            textColor="white"
            onClick={() => onApplyFilter()}
            width="120px"
            height="36px"
          />
        </div>
      </div>
      <div style={{ overflowX: "auto", opacity: loading ? 0.5 : 1, transition: "opacity 0.15s" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={thStyle}>Artist Name</th>
              <th style={thStyle}>Date Joined</th>
              <th style={thStyle}>Distribution Fee</th>

              <th style={thStyle}>All-Time Streams</th>
              <th style={thStyle}>All-Time Gross Rev.</th>
              <th style={thStyle}>All-Time Artist Rev.</th>
              <th style={thStyle}>All-Time Kahuna Rev.</th>
              <th style={thStyle}>All-Time Platform Streams</th>
              <th style={thStyle}>All-Time Platform Rev.</th>

              <th style={thStyle}>LTM Streams</th>
              <th style={thStyle}>LTM Gross Rev.</th>
              <th style={thStyle}>LTM Artist Rev.</th>
              <th style={thStyle}>LTM Kahuna Rev.</th>
              <th style={thStyle}>LTM Platform Streams</th>
              <th style={thStyle}>LTM Platform Rev.</th>

              <th style={thStyle}>LM Streams</th>
              <th style={thStyle}>LM Gross Rev.</th>
              <th style={thStyle}>LM Artist Rev.</th>
              <th style={thStyle}>LM Kahuna Rev.</th>
              <th style={thStyle}>LM Platform Streams</th>
              <th style={thStyle}>LM Platform Rev.</th>

              <th style={thStyle}>Exclude</th>
            </tr>
          </thead>
          <tbody>
            {rows
              .sort((a, b) => (a.all.totalAmount < b.all.totalAmount ? 1 : -1))
              .map((row) => renderDistributionRow(row, onToggleExclude))}
            {rows.length === 0 && (
              <tr>
                <td style={{ ...tdStyle, textAlign: "center", color: TEXT_MUTED }} colSpan={11}>
                  No distribution deals
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <Pagination hidden={pageCount <= 1} currentPage={page} pageCount={pageCount} onClickPage={onPageChange} />
    </div>
  )
}

// ---- main -------------------------------------------------------------------

const AdminPortfolioOverview: FC<AdminPortfolioOverviewProps> = ({ setOpenSidebar, openSidebar }) => {
  // Full-page spinner only on the very first summary load; later fetches keep
  // the page mounted and dim the affected table instead.
  const [initialLoading, setInitialLoading] = useState(true)
  const [summaryKpi, setSummaryKpi] = useState<AdvancePortfolioSummaryKpi | null>(null)

  // Heavy, engine-based analytics — loaded on demand ("Click to calculate"),
  // mirroring the Performance tab so the page renders instantly on open.
  const [portfolioKpi, setPortfolioKpi] = useState<AdvancePortfolioKpi | null>(null)
  const [advancePortfolioTable, setAdvancePortfolioTable] = useState<AdvancePortfolioTable | null>(null)
  const [distributionPortfolioTable, setDistributionPortfolioTable] = useState<DistributionPortfolioTable | null>(null)
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false)
  const [distributionEnabled, setDistributionEnabled] = useState(false)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [advanceLoading, setAdvanceLoading] = useState(false)
  const [distributionLoading, setDistributionLoading] = useState(false)
  const [showExcluded, setShowExcluded] = useState(false)
  const [selectedDeal, setSelectedDeal] = useState<AdvanceDealRow>()
  const [dateValues, setDateValues] = useState<DateRangeType>([null, null])

  // ---- per-table search / filter / page state (server-side) ----------------
  const [summaryPage, setSummaryPage] = useState(1)

  const [advStatus, setAdvStatus] = useState<KSelectOption[]>([])
  const [advAge, setAdvAge] = useState<KSelectOption | undefined>()
  const [royaltyDateFilter, setRoyaltyDateFilter] = useState<KSelectOption | undefined>(RoyaltyDateOptionList[1])
  const [advExposure, setAdvExposure] = useState<KSelectOption | undefined>()
  const [advContractExpiry, setAdvContractExpiry] = useState<KSelectOption | undefined>()
  // Default sort: nearest contract end first, so expiring deals surface on top.
  const [contractEndSort, setContractEndSort] = useState<"asc" | "desc" | undefined>("asc")
  const [advPage, setAdvPage] = useState(1)

  const [distroFee, setDistroFee] = useState<KSelectOption | undefined>()
  const [accountType, setAccountType] = useState<KSelectOption | undefined>()
  const [distroPage, setDistroPage] = useState(1)
  const [selectedUser, setSelectedUser] = useState<KSelectOption | undefined>()

  const fetchSummaryKpi = () => {
    const query = `?show_excluded=${showExcluded}`

    users(`/user_advance_portfolio_summary_kpi/${query}`, "GET")
      .then((response) => {
        setSummaryKpi(mapToCamelCase<AdvancePortfolioSummaryKpi>(response.data))
        setInitialLoading(false)
      })
      .catch(() => {})
  }

  const fetchAdvancePortfolioKpi = () => {
    setAnalyticsLoading(true)
    const query = `?show_excluded=${showExcluded}`

    users(`/user_advance_portfolio_kpi/${query}`, "GET")
      .then((response) => {
        setPortfolioKpi(mapToCamelCase<AdvancePortfolioKpi>(response.data))
      })
      .catch(() => setAnalyticsLoading(false))
  }

  const fetchAdvancePortfolioTable = (offset: number) => {
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const dealStatusParam =
      advStatus.length > 0 ? `&deal_status=${advStatus.map((option) => option.value2).join(",")}` : ""
    const recoupAgeParam = advAge ? `&deal_recoup_age=${advAge.value2}` : ""
    const royaltyDateParam = royaltyDateFilter ? `&royalty_date=${royaltyDateFilter.value2}` : ""
    const advanceTotalParam = advExposure ? `&advance_total=${advExposure.value2}` : ""
    const contractExpiryParam = advContractExpiry ? `&contract_end_within_days=${advContractExpiry.value2}` : ""
    const contractEndSortParam = contractEndSort ? `&contract_end_sort=${contractEndSort}` : ""

    const query = `?show_excluded=${showExcluded}${userIdParam}&offset=${offset}&limit=${PAGE_SIZE}${dealStatusParam}${recoupAgeParam}${advanceTotalParam}${royaltyDateParam}${contractExpiryParam}${contractEndSortParam}`

    setAdvanceLoading(true)
    users(`/user_advance_portfolio_table/${query}`, "GET")
      .then((response) => {
        setAdvancePortfolioTable(mapToCamelCase<AdvancePortfolioTable>(response.data))
        setAdvanceLoading(false)
      })
      .catch(() => setAdvanceLoading(false))
  }

  const fetchDistributionPortfolioTable = (offset: number) => {
    const userIdParam = selectedUser ? `&user_id=${selectedUser.value}` : ""
    const distroFeeParam = distroFee ? `&distribution_fee=${distroFee.value2}` : ""
    const accountTypeParam = accountType ? `&account_type=${accountType.value2}` : ""
    const dateParam =
      //@ts-ignore
      dateValues && dateValues?.[0] && dateValues?.[1]
        ? //@ts-ignore
          `&date=${dateToYYYYMM(dateValues[0])},${dateToYYYYMM(dateValues[1])}`
        : ""
    const query = `?show_excluded=${showExcluded}${userIdParam}${distroFeeParam}${accountTypeParam}${dateParam}&offset=${offset}&limit=${PAGE_SIZE}`

    setDistributionLoading(true)
    users(`/user_distribution_portfolio_table/${query}`, "GET")
      .then((response) => {
        setDistributionPortfolioTable(mapToCamelCase<DistributionPortfolioTable>(response.data))
        setDistributionLoading(false)
      })
      .catch(() => setDistributionLoading(false))
  }

  useEffect(() => {
    fetchSummaryKpi()
  }, [showExcluded])

  // Full analytics — fetched once enabled, then on any table param change.
  useEffect(() => {
    if (!analyticsEnabled) return
    fetchAdvancePortfolioKpi()
  }, [analyticsEnabled, showExcluded])

  useEffect(() => {
    fetchAdvancePortfolioTable((advPage - 1) * PAGE_SIZE)
  }, [showExcluded, advPage, contractEndSort])

  // Header click cycles: ascending (nearest end first) → descending → default order.
  const onToggleContractEndSort = () => {
    setContractEndSort((prev) => (prev === "asc" ? "desc" : prev === "desc" ? undefined : "asc"))
    setAdvPage(1)
  }

  useEffect(() => {
    if (!distributionEnabled) return
    fetchDistributionPortfolioTable((distroPage - 1) * PAGE_SIZE)
  }, [showExcluded, distroPage])

  const onApplyAdvanceFilter = () => {
    fetchAdvancePortfolioTable((summaryPage - 1) * PAGE_SIZE)
  }

  const onApplyDistributionFilter = () => {
    setDistributionEnabled(true)
    fetchDistributionPortfolioTable((summaryPage - 1) * PAGE_SIZE)
  }

  const onToggleShowExcluded = () => {
    setShowExcluded((prev) => !prev)
    setSummaryPage(1)
    setAdvPage(1)
    setDistroPage(1)
  }

  const onToggleExclude = (userId: number, currentlyExcluded: boolean, isAdvanceDeal: boolean) => {
    users("/update_exclude_portfolio/", "PUT", {
      user_id: userId,
      exclude_from_portfolio: !currentlyExcluded
    }).then((resp) => {
      if (!resp.data.success) {
        toast.error("Failed to update portfolio exclusion status", errorFilledToastOptions)
        return
      }
      toast.success("Portfolio exclusion status updated", successFilledToastOptions)
      if (isAdvanceDeal) {
        setAdvancePortfolioTable((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            advanceDeals: prev.advanceDeals.map((deal) =>
              deal.userId === userId ? { ...deal, excluded: !currentlyExcluded } : deal
            )
          }
        })
      } else {
        setDistributionPortfolioTable((prev) => {
          if (!prev) return prev
          return {
            ...prev,
            distributionDeals: prev.distributionDeals.map((deal) =>
              deal.userId === userId ? { ...deal, excluded: !currentlyExcluded } : deal
            )
          }
        })
      }
    })
  }

  // Search / filter changes reset their table to the first page.
  const onAdvFilterChange =
    (setter: (option: KSelectOption | KSelectOption[] | undefined) => void) =>
    (option: KSelectOption | KSelectOption[] | undefined) => {
      setter(option)
      setAdvPage(1)
    }

  const onDistroFilterChange =
    (setter: (option: KSelectOption | KSelectOption[] | undefined) => void) =>
    (option: KSelectOption | KSelectOption[] | undefined) => {
      setter(option)
      setDistroPage(1)
    }

  if (selectedDeal) {
    return <AdvanceDealDetailPage selectedDeal={selectedDeal} onBack={() => setSelectedDeal(undefined)} />
  }

  if (initialLoading) {
    return (
      <div className="p-8 flex justify-center items-center min-h-[400px]">
        <Loading />
      </div>
    )
  }

  const calculated = portfolioKpi !== null

  const renderWarningBlock = (portfolioKpi: AdvancePortfolioKpi) => {
    let warningText = ""
    if (portfolioKpi.alert.watchlistCount) warningText += `${portfolioKpi.alert.watchlistCount} Watch List (0-6) --- `
    if (portfolioKpi.alert.delayedCount) warningText += `${portfolioKpi.alert.delayedCount} Delayed (6-18) ---`
    if (portfolioKpi.alert.impairedCount) warningText += `${portfolioKpi.alert.impairedCount} Impaired (18+)`

    return (
      <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200 flex-wrap">
        <span style={{ color: "#f59e0b", fontSize: 16 }}>⚠</span>
        <KSpan
          text={`${portfolioKpi.alert.dealsNeedAttention} deals need attention`}
          fontSize={13}
          fontWeight={600}
          color="#92400e"
        />
        <span style={{ color: "#d6b370" }}>|</span>
        <KSpan
          text={`${usd0(portfolioKpi.alert.flaggedOutstanding)} flagged outstanding`}
          fontSize={13}
          color="#92400e"
        />
        <span style={{ color: "#d6b370" }}>|</span>
        <KSpan text={warningText} fontSize={13} color="#92400e" />
      </div>
    )
  }

  return (
    <div className="px-10 pt-5 pb-10 flex flex-col gap-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <KTooltip
            backgroundColor="#000"
            padding="0px 4px"
            height="20px"
            arrowColor="#000"
            position="bottom"
            content={
              <div className="h-5 w-max flex items-center">
                <KSpan
                  text={openSidebar ? "Hide sidebar" : "Open sidebar"}
                  fontSize={12}
                  lineHeight="16px"
                  color="#FFF"
                />
              </div>
            }
          >
            <KButton
              dataTestId="portfolio-overview-sidebar-toggle-button"
              icon="/left-sidebar.svg"
              width="32px"
              height="32px"
              background="transparent"
              shadowDisabled={true}
              padding="6px"
              onClick={() => setOpenSidebar(!openSidebar)}
            />
          </KTooltip>
          <KTitleSpan text="Portfolio Overview" fontSize={24} fontWeight={700} color={TEXT_PRIMARY} />
        </div>
        <div className="flex flex-row items-center gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showExcluded}
              onChange={onToggleShowExcluded}
              style={{ accentColor: "#111", cursor: "pointer" }}
            />
            <KSpan text="Show Excluded" fontSize={13} color={TEXT_MUTED} />
          </label>
          {!calculated && (
            <KButton
              text={analyticsLoading ? "Calculating..." : "Calculate full analytics"}
              onClick={() => setAnalyticsEnabled(true)}
              disabled={analyticsLoading}
              background={"black"}
              textColor={"white"}
            />
          )}
        </div>
      </div>

      {/* KPI cards — deployed/outstanding are instant; the rest are engine-based */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
        <KpiCard
          label="TOTAL DEPLOYED"
          value={usd0(summaryKpi?.totalDeployed)}
          subtext={`${summaryKpi?.totalAdvanceDeals} advance deals`}
        />
        {calculated && portfolioKpi ? (
          <>
            <KpiCard
              label="OUTSTANDING"
              value={usd0(portfolioKpi?.outstanding)}
              subtext={`${pct(portfolioKpi?.outstandingPctOfDeployed)} of deployed`}
            />
            <KpiCard
              label="RECOUP TARGET"
              value={usd0(portfolioKpi.recoupTarget)}
              subtext={`Avg multiple ${multipleX(portfolioKpi.avgMultiple)}`}
            />
            <KpiCard
              label="FLAGGED EXPOSURE"
              value={usd0(portfolioKpi.flaggedExposure)}
              subtext={`${pct(portfolioKpi.flaggedExposurePct)} of outstanding`}
              subtextColor={portfolioKpi.flaggedExposure > 0 ? RED : TEXT_MUTED}
            />
            <KpiCard label="RECOUP LAST 30D" value={usd0(portfolioKpi.recoupLast30D)} subtext="Company-side" />
            <KpiCard label="12M IRR" value={pct(portfolioKpi.irr12M)} subtext="Normalized 12M IRR" />
            <KpiCard label="24M IRR" value={pct(portfolioKpi.irr24M)} subtext="Normalized 24M IRR" />
            <KpiCard label="36M IRR" value={pct(portfolioKpi.irr36M)} subtext="Normalized 36M IRR" />
            <KpiCard label="PORTFOLIO XIRR" value={pct(portfolioKpi.portfolioXirr)} subtext="All cash flows" />
            <KpiCard label="ECONOMIC MOIC" value={multipleX(portfolioKpi.economicMoic)} subtext="Recoup + commission" />
          </>
        ) : (
          [
            "OUTSTANDING",
            "RECOUP TARGET",
            "FLAGGED EXPOSURE",
            "RECOUP LAST 30D",
            "12M NORMALIZED IRR",
            "24M NORMALIZED IRR",
            "36M NORMALIZED IRR",
            "PORTFOLIO XIRR",
            "ECONOMIC MOIC"
          ].map((label) => (
            <BlurredKpiCard
              key={label}
              label={label}
              isLoading={analyticsLoading}
              onClick={() => setAnalyticsEnabled(true)}
            />
          ))
        )}
      </div>

      {/* Charts + alert + full tables only after analytics are calculated */}
      {calculated && portfolioKpi && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <KTitleSpan text="Estimated Company Recoup Time" fontSize={16} fontWeight={700} />
              <div style={{ height: 240, marginTop: 16 }}>
                <RecoupTimeChart portfolio={portfolioKpi} />
              </div>
              <div className="flex gap-3 mt-4">
                <SummaryCard
                  label="AVG RECOUP TIME"
                  value={
                    portfolioKpi.recoupTimeChart.avgRecoupTime === null
                      ? "N/A"
                      : `${portfolioKpi.recoupTimeChart.avgRecoupTime} Months`
                  }
                />
                <SummaryCard label="RECOUPED ADVANCES" value={`${portfolioKpi.recoupTimeChart.recoupedPct}%`} />
              </div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <KTitleSpan text="Monthly Deployed vs Recouped" fontSize={16} fontWeight={700} />
              <div style={{ height: 240, marginTop: 16 }}>
                <MonthlyChart portfolio={portfolioKpi} />
              </div>
              <div className="flex gap-3 mt-4">
                <SummaryCard label="ON-TRACK DEALS" value={`${portfolioKpi.monthlyChart.onTrackPct}%`} />
                <SummaryCard label="NPL DEALS" value={`${portfolioKpi.monthlyChart.nplTrackPct}%`} />
              </div>
            </div>
          </div>
          {portfolioKpi.alert.dealsNeedAttention > 0 && renderWarningBlock(portfolioKpi)}
        </>
      )}

      {advancePortfolioTable && (
        <AdvanceDealsTable
          rows={advancePortfolioTable.advanceDeals}
          count={advancePortfolioTable.advanceDealsCount}
          page={advPage}
          statusFilter={advStatus}
          ageFilter={advAge}
          royaltyDateFilter={royaltyDateFilter}
          exposureFilter={advExposure}
          contractExpiryFilter={advContractExpiry}
          contractEndSort={contractEndSort}
          selectedUser={selectedUser}
          setSelectedUser={setSelectedUser}
          loading={advanceLoading}
          onApplyFilter={onApplyAdvanceFilter}
          //@ts-ignore
          onStatusChange={onAdvFilterChange(setAdvStatus)}
          //@ts-ignore
          onAgeChange={onAdvFilterChange(setAdvAge)}
          //@ts-ignore
          onRoyaltyDateChange={onAdvFilterChange(setRoyaltyDateFilter)}
          //@ts-ignore
          onExposureChange={onAdvFilterChange(setAdvExposure)}
          //@ts-ignore
          onContractExpiryChange={onAdvFilterChange(setAdvContractExpiry)}
          onToggleContractEndSort={onToggleContractEndSort}
          onPageChange={setAdvPage}
          onToggleExclude={onToggleExclude}
          onSelectDeal={setSelectedDeal}
        />
      )}
      {distributionPortfolioTable ? (
        <DistributionDealsTable
          rows={distributionPortfolioTable.distributionDeals}
          count={distributionPortfolioTable.distributionDealsCount}
          page={distroPage}
          selectedUser={selectedUser}
          dateValues={dateValues}
          onApplyFilter={onApplyDistributionFilter}
          setSelectedUser={setSelectedUser}
          distroFee={distroFee}
          accountType={accountType}
          loading={distributionLoading}
          //@ts-ignore
          onDistroFeeChange={onDistroFilterChange(setDistroFee)}
          //@ts-ignore
          onAccountTypeChange={onDistroFilterChange(setAccountType)}
          setDateValues={setDateValues}
          onPageChange={setDistroPage}
          onToggleExclude={onToggleExclude}
        />
      ) : (
        <div className="mt-4 flex justify-center">
          <KButton
            width="240px"
            background="black"
            textColor="white"
            text="Load Distribution Table"
            onClick={() => onApplyDistributionFilter()}
          />
        </div>
      )}
    </div>
  )
}

export default AdminPortfolioOverview
